import { useContext, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";

import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import HollowButton from "components/HollowButton";

import ModalsManagerContext from "contexts/ModalsManager";
import PackagesContext from "contexts/PackagesContext";

import decreeService from "api/decreeService";
import parcelService from "api/parcelService";

import { IPackage } from "dto/IPackage";
import { IParcelReceiver } from "dto/Mailroom/IMailroom";
import {
  DECREE_PRIORITY_LABELS,
  DECREE_TYPE_LABELS,
  EDecreePriority,
  EDecreeType,
  IDecree,
  decreeTransfersOwnership,
} from "dto/Decree/IDecree";

import { getUserId } from "utils/getUserId";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

const DECREE_TYPE_OPTIONS: EDecreeType[] = [
  EDecreeType.Custom,
  EDecreeType.ReplyDeadline,
  EDecreeType.ConsultationRequest,
  EDecreeType.ByCharacteristic,
  EDecreeType.CourtesyCopy,
  EDecreeType.CourtesyCopyWithConfirmation,
];

const DEFAULT_DEADLINE_DAYS: Partial<Record<EDecreeType, number>> = {
  [EDecreeType.ReplyDeadline]: 30,
  [EDecreeType.ConsultationRequest]: 14,
};

const isCourtesy = (type: EDecreeType) =>
  type === EDecreeType.CourtesyCopy ||
  type === EDecreeType.CourtesyCopyWithMultipleUsers ||
  type === EDecreeType.CourtesyCopyWithConfirmation;

const todayIso = () => new Date().toISOString().slice(0, 10);

const addDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const DecreeModal = () => {
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const { modals, setModals } = useContext(ModalsManagerContext);
  const { packages, setPackages } = useContext(PackagesContext);

  const pack = useMemo<IPackage | null>(() => {
    const raw = localStorage.getItem("package");
    return raw ? (JSON.parse(raw) as IPackage) : null;
  }, []);

  const workspaceId = getWorkspaceId() ?? undefined;
  const { data: initData } = parcelService.useInitParcel(workspaceId);
  const receivers: IParcelReceiver[] = useMemo(
    () => (initData?.data?.Receivers ?? []).filter((r) => !r.IsMe),
    [initData],
  );

  const {
    data: decreesData,
    refetch: refetchDecrees,
  } = decreeService.useGetParcelDecrees(pack?.Id ?? "");
  const decrees: IDecree[] = decreesData?.data?.Decrees ?? [];

  const { mutate: addDecree } = decreeService.useAddDecree();
  const closeMutation = decreeService.useCloseDecree();
  const deleteMutation = decreeService.useDeleteDecree();
  const confirmMutation = decreeService.useConfirmDecree();
  const returnMutation = decreeService.useReturnParcel();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Zwrot przesyłki z powodem (Magistrat: „Zwrot") — do autora dekretu albo biura podawczego.
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  // Zwrot ma sens tylko, gdy przesyłka została mi przekazana (jest ślad przekazania
  // albo dekret transferujący do mnie) — inaczej nie ma do kogo jej zwrócić.
  const canReturn =
    !!pack &&
    (!!pack.Pass ||
      decrees.some(
        (d) => d.TransfersOwnership && d.ReceiverUserId === userId && !d.Inactive,
      ));

  // Formularz nowego dekretu
  const [type, setType] = useState<EDecreeType>(EDecreeType.Custom);
  const [receiverIds, setReceiverIds] = useState<string[]>([]);
  const [priority, setPriority] = useState<EDecreePriority>(
    EDecreePriority.Normal,
  );
  const [deadline, setDeadline] = useState<string>("");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Zamykanie dekretu (inline)
  const [closingId, setClosingId] = useState<string | null>(null);
  const [closePartly, setClosePartly] = useState(false);
  const [closeDesc, setCloseDesc] = useState("");
  const [closeDate, setCloseDate] = useState<string>(todayIso());

  const close = () => setModals({ ...modals, decree: false });

  const onTypeChange = (next: EDecreeType) => {
    setType(next);
    const days = DEFAULT_DEADLINE_DAYS[next];
    setDeadline(days ? addDays(days) : "");
    if (!isCourtesy(next) && receiverIds.length > 1) {
      setReceiverIds(receiverIds.slice(0, 1));
    }
  };

  const resetForm = () => {
    setReceiverIds([]);
    setTitle("");
    setText("");
    setDeadline(DEFAULT_DEADLINE_DAYS[type] ? addDays(DEFAULT_DEADLINE_DAYS[type]!) : "");
  };

  const handleAdd = async () => {
    if (!pack) return;
    setError("");

    if (!receiverIds.length) {
      setError("Wskaż odbiorcę dekretu.");
      return;
    }
    const transfers = decreeTransfersOwnership(type);
    if (transfers && receiverIds.length !== 1) {
      setError("Dekret przekazujący własność może mieć tylko jednego odbiorcę.");
      return;
    }

    setIsLoading(true);
    // Odbiorcy jeszcze nieutworzeni — na wypadek częściowej awarii pętli ograniczamy retry.
    const remaining = [...receiverIds];
    try {
      // Model kluczy firmowych: odbiorca dekretu jest członkiem tej samej firmy i czyta
      // przesyłkę kluczem FIRMY — nie przeszyfrowujemy kluczy obiektu per odbiorca.
      for (const receiverId of [...remaining]) {
        const receiver = receivers.find((r) => r.Id === receiverId);
        if (!receiver) throw new Error("Nie znaleziono odbiorcy.");

        await new Promise<void>((resolve, reject) => {
          addDecree(
            {
              ParcelId: pack.Id,
              ReceiverUserId: receiverId,
              Type: type,
              Priority: priority,
              Deadline: deadline ? new Date(deadline).toISOString() : null,
              Title: title || null,
              Text: text || null,
            },
            {
              onSuccess: () => resolve(),
              onError: (e) => reject(e),
            },
          );
        });
        remaining.shift();
      }

      notification({
        type: ENotificationType.SUCCESS,
        title: "Zadekretowano",
        text: transfers
          ? "Przesyłka została przekazana."
          : "Dekret został dodany.",
      });

      if (transfers) {
        // Przesyłka opuściła moją listę Przychodzących.
        setPackages(packages.filter((p) => p.Id !== pack.Id));
        close();
      } else {
        resetForm();
        refetchDecrees();
      }
    } catch (e) {
      // Częściowy sukces (pętla wielu odbiorców): zostaw do wysłania tylko nieutworzonych
      // i odśwież listę, żeby pokazać już zapisane kopie i nie duplikować ich przy retry.
      setReceiverIds(remaining);
      refetchDecrees();
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd dekretacji",
        text: (e as Error)?.message ?? "Nie udało się dodać dekretu.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startClose = (decree: IDecree) => {
    setClosingId(decree.Id);
    setClosePartly(false);
    setCloseDesc("");
    setCloseDate(todayIso());
  };

  const submitClose = () => {
    if (!closingId || closeMutation.isLoading) return;
    if (closePartly && !closeDesc.trim()) {
      setError("Podaj opis częściowego wykonania.");
      return;
    }
    setError("");
    closeMutation.mutate(
      {
        DecreeId: closingId,
        IsPartlyClosed: closePartly,
        PartlyClosedDescription: closePartly ? closeDesc : null,
        ClosingDate: new Date(closeDate).toISOString(),
      },
      {
        onSuccess: () => {
          setClosingId(null);
          refetchDecrees();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się zamknąć dekretu.",
          }),
      },
    );
  };

  const handleConfirm = (decree: IDecree) => {
    if (confirmMutation.isLoading) return;
    confirmMutation.mutate(
      { DecreeId: decree.Id },
      {
        onSuccess: () => refetchDecrees(),
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się potwierdzić zapoznania.",
          }),
      },
    );
  };

  const submitReturn = () => {
    if (!pack || returnMutation.isLoading) return;
    if (!returnReason.trim()) {
      setError("Podaj powód zwrotu.");
      return;
    }
    setError("");
    returnMutation.mutate(
      { ParcelId: pack.Id, Reason: returnReason.trim() },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Zwrócono",
            text: "Przesyłka wróciła do osoby, która ją przekazała.",
          });
          setPackages(packages.filter((p) => p.Id !== pack.Id));
          close();
        },
        onError: (e: unknown) => {
          const msg =
            (e as { response?: { data?: { errorCodes?: { message: string }[] } } })
              ?.response?.data?.errorCodes?.[0]?.message ??
            "Nie udało się zwrócić przesyłki.";
          setError(msg);
        },
      },
    );
  };

  const handleDelete = (decree: IDecree) => {
    if (deleteMutation.isLoading) return;
    setPendingDeleteId(decree.Id);
    deleteMutation.mutate(decree.Id, {
      onSuccess: () => refetchDecrees(),
      onSettled: () => setPendingDeleteId(null),
      onError: () =>
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: "Nie udało się usunąć dekretu.",
        }),
    });
  };

  // Lewa linia w kolorach Noty: pełny akcent dla nieprzeczytanych, neutralny dla
  // zamkniętych/nieaktywnych, miękki fiolet dla pozostałych (z SCSS ::before).
  const decreeStatusClass = (d: IDecree) => {
    if (d.Inactive) return styles.inactive;
    if (d.IsClosed) return styles.closed;
    if (d.IsUnread) return styles.unread;
    return "";
  };

  // Pilność terminu sygnalizujemy na tekście „Termin", nie na brandowej linii.
  const termClass = (d: IDecree) => {
    if (d.IsExpired) return styles.termExpired;
    if (d.IsAboutToExpire) return styles.termWarn;
    return "";
  };

  const decreeStatusLabel = (d: IDecree) => {
    if (d.Type === EDecreeType.Return) return "Zwrot";
    if (d.RequiresConfirmation && d.ConfirmationTimestamp) return "Potwierdzony";
    if (d.RequiresConfirmation && !d.IsClosed) return "Czeka na potwierdzenie";
    if (d.IsClosed) return "Wykonany";
    if (d.IsPartlyClosed) return "Wykonany częściowo";
    if (d.Inactive) return "Nieaktywny";
    if (d.IsExpired) return "Po terminie";
    if (d.IsAboutToExpire) return "Zbliża się termin";
    return "Otwarty";
  };

  const multi = isCourtesy(type);

  return (
    <div className={styles.overlay} onMouseDown={close}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.title}>
          <div>
            Dekretuj przesyłkę
            {pack?.S1 && <div className={styles.subtitle}>{pack.S1}</div>}
          </div>
          <CloseIcon className={styles.closeIcon} onClick={close} />
        </div>

        <div className={styles.content}>
          {/* ── Istniejące dekrety ─────────────────────────────── */}
          <div>
            <div className={styles.sectionHeader}>Dekrety przesyłki</div>
            {decrees.length === 0 ? (
              <div className={styles.empty}>Brak dekretów.</div>
            ) : (
              <div className={styles.decreeList}>
                {decrees.map((d) => (
                  <div
                    key={d.Id}
                    className={`${styles.decreeItem} ${decreeStatusClass(d)}`}
                  >
                    <div className={styles.decreeHead}>
                      <span className={styles.decreeType}>
                        {DECREE_TYPE_LABELS[d.Type]}
                      </span>
                      <span
                        className={`${styles.badge} ${
                          d.TransfersOwnership
                            ? styles.badgeTransfer
                            : styles.badgeCopy
                        }`}
                      >
                        {d.Type === EDecreeType.Return
                          ? "zwrot"
                          : d.TransfersOwnership
                          ? "przekazanie"
                          : d.RequiresConfirmation
                          ? "za potwierdzeniem"
                          : "do wiadomości"}
                      </span>
                    </div>
                    <div className={styles.decreeMeta}>
                      <span>Do: {d.ReceiverLabel ?? "—"}</span>
                      <span>Od: {d.CreatedByLabel ?? "—"}</span>
                      {d.Deadline && (
                        <span className={termClass(d)}>
                          Termin:{" "}
                          {new Date(d.Deadline).toLocaleDateString("pl-PL")}
                        </span>
                      )}
                      <span>Status: {decreeStatusLabel(d)}</span>
                    </div>
                    {d.Text && <div className={styles.decreeText}>{d.Text}</div>}
                    {d.PartlyClosedDescription && (
                      <div className={styles.decreeText}>
                        Wykonano częściowo: {d.PartlyClosedDescription}
                      </div>
                    )}

                    {!d.IsClosed && !d.Inactive && (
                      <div className={styles.decreeActions}>
                        {d.RequiresConfirmation && d.ReceiverUserId === userId ? (
                          <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => handleConfirm(d)}
                            disabled={confirmMutation.isLoading}
                          >
                            Potwierdzam zapoznanie
                          </button>
                        ) : !d.RequiresConfirmation ? (
                          <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => startClose(d)}
                          >
                            Oznacz jako wykonany
                          </button>
                        ) : null}
                        {d.CreatedByUserId === userId && (
                          <button
                            type="button"
                            className={`${styles.linkBtn} ${styles.danger}`}
                            onClick={() => handleDelete(d)}
                            disabled={
                              deleteMutation.isLoading && pendingDeleteId === d.Id
                            }
                          >
                            Usuń
                          </button>
                        )}
                      </div>
                    )}

                    {closingId === d.Id && (
                      <div className={styles.closeForm}>
                        <RadioGroup
                          row
                          value={closePartly ? "partly" : "full"}
                          onChange={(e) =>
                            setClosePartly(e.target.value === "partly")
                          }
                        >
                          <FormControlLabel
                            value="full"
                            control={<Radio size="small" />}
                            label="Wykonano"
                          />
                          <FormControlLabel
                            value="partly"
                            control={<Radio size="small" />}
                            label="Wykonano częściowo"
                          />
                        </RadioGroup>
                        {closePartly && (
                          <TextField
                            size="small"
                            label="Opis częściowego wykonania"
                            value={closeDesc}
                            onChange={(e) => setCloseDesc(e.target.value)}
                            fullWidth
                          />
                        )}
                        <input
                          type="date"
                          className={styles.dateInput}
                          value={closeDate}
                          onChange={(e) => setCloseDate(e.target.value)}
                        />
                        <div className={styles.closeFormActions}>
                          <Button
                            text="Zapisz"
                            onClick={submitClose}
                            loading={closeMutation.isLoading}
                          />
                          <HollowButton
                            text="Anuluj"
                            onClick={() => setClosingId(null)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Dodaj dekret ───────────────────────────────────── */}
          <div className={styles.row}>
            <div className={styles.sectionHeader}>Dodaj dekret</div>

            <FormControl fullWidth size="small">
              <InputLabel>Rodzaj dekretu</InputLabel>
              <Select
                label="Rodzaj dekretu"
                value={type}
                onChange={(e) => onTypeChange(Number(e.target.value) as EDecreeType)}
              >
                {DECREE_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {DECREE_TYPE_LABELS[t]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Odbiorca</InputLabel>
              <Select
                label="Odbiorca"
                multiple={multi}
                value={multi ? receiverIds : receiverIds[0] ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setReceiverIds(
                    Array.isArray(v) ? (v as string[]) : v ? [v as string] : [],
                  );
                }}
                renderValue={(selected) => {
                  const ids = Array.isArray(selected)
                    ? (selected as string[])
                    : [selected as string];
                  return ids
                    .map((id) => receivers.find((r) => r.Id === id)?.Label ?? id)
                    .join(", ");
                }}
              >
                {receivers.map((r) => (
                  <MenuItem key={r.Id} value={r.Id}>
                    {r.Label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <div className={styles.twoCol}>
              <FormControl fullWidth size="small">
                <InputLabel>Priorytet</InputLabel>
                <Select
                  label="Priorytet"
                  value={priority}
                  onChange={(e) =>
                    setPriority(Number(e.target.value) as EDecreePriority)
                  }
                >
                  {[
                    EDecreePriority.Low,
                    EDecreePriority.Normal,
                    EDecreePriority.High,
                  ].map((p) => (
                    <MenuItem key={p} value={p}>
                      {DECREE_PRIORITY_LABELS[p]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <div>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  placeholder="Termin"
                />
              </div>
            </div>

            <TextField
              size="small"
              label="Tytuł"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label="Treść dekretu"
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />

            {error && <div className={styles.error}>{error}</div>}
          </div>

          {/* ── Zwrot przesyłki ────────────────────────────────── */}
          {canReturn && (
            <div className={styles.row}>
              <div className={styles.sectionHeader}>Zwrot przesyłki</div>
              {!returnOpen ? (
                <div className={styles.decreeActions}>
                  <button
                    type="button"
                    className={`${styles.linkBtn} ${styles.danger}`}
                    onClick={() => setReturnOpen(true)}
                  >
                    Zwróć przesyłkę do osoby, która ją przekazała
                  </button>
                </div>
              ) : (
                <>
                  <TextField
                    size="small"
                    label="Powód zwrotu"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <div className={styles.closeFormActions}>
                    <Button
                      text="Zwróć"
                      onClick={submitReturn}
                      loading={returnMutation.isLoading}
                    />
                    <HollowButton
                      text="Anuluj"
                      onClick={() => {
                        setReturnOpen(false);
                        setReturnReason("");
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <HollowButton text="Zamknij" onClick={close} />
          <Button text="Dodaj dekret" onClick={handleAdd} loading={isLoading} />
        </div>
      </div>
    </div>
  );
};

export default DecreeModal;
